import { readInvokeError } from "@/lib/invokeError";
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Loader2, AlertCircle, Eye, EyeOff, CheckCircle2, Smartphone, Clock, RefreshCw } from "lucide-react";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Progress } from "@/components/ui/progress";

type AuthState = 
  | 'idle'
  | 'authenticating'
  | 'requesting_otp'
  | 'checkpoint_otp'
  | 'checkpoint_2fa'
  | 'checkpoint_in_app'
  | 'checkpoint_captcha'
  | 'syncing_data'
  | 'success'
  | 'error';

interface ReconnectClientDialogProps {
  clientId: string;
  clientEmail: string;
  providerAccountId?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function ReconnectClientDialog({ 
  clientId, 
  clientEmail,
  open, 
  onOpenChange, 
  onSuccess 
}: ReconnectClientDialogProps) {
  const [authState, setAuthState] = useState<AuthState>('idle');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Form fields - email is pre-filled
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // Checkpoint fields
  const [otpCode, setOtpCode] = useState("");
  const [accountId, setAccountId] = useState("");
  const [checkpointMessage, setCheckpointMessage] = useState("");
  
  // IN_APP_VALIDATION polling
  const [timeRemaining, setTimeRemaining] = useState(300);
  const [pollInterval, setPollInterval] = useState<NodeJS.Timeout | null>(null);

  // Sync progress tracking
  const [syncProgress, setSyncProgress] = useState({
    profile: false,
    inbox: false,
    newMessagesCount: 0
  });

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [pollInterval]);

  // Countdown timer for IN_APP_VALIDATION
  useEffect(() => {
    if (authState === 'checkpoint_in_app' && timeRemaining > 0) {
      const timer = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            setAuthState('error');
            setError('Verification timed out. Please try again.');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [authState, timeRemaining]);

  // Start polling for IN_APP_VALIDATION
  useEffect(() => {
    if (authState === 'checkpoint_in_app' && accountId) {
      startPolling();
    }
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        setPollInterval(null);
      }
    };
  }, [authState, accountId]);

  const resetState = () => {
    setAuthState('idle');
    setLoading(false);
    setError("");
    setPassword("");
    setShowPassword(false);
    setOtpCode("");
    setAccountId("");
    setCheckpointMessage("");
    setTimeRemaining(300);
    setSyncProgress({ profile: false, inbox: false, newMessagesCount: 0 });
    if (pollInterval) {
      clearInterval(pollInterval);
      setPollInterval(null);
    }
  };

  const performPostConnectionSync = async () => {
    setAuthState('syncing_data');
    // Mark inbox as done (skipped) - we only sync profile for faster reconnection
    setSyncProgress({ profile: false, inbox: true, newMessagesCount: 0 });

    try {
      // Profile sync only - inbox sync is skipped for speed
      const { data, error } = await supabase.functions.invoke('sync-client-status', {
        body: { clientId }
      });

      const profileSuccess = !error && data?.success !== false;
      setSyncProgress({ profile: profileSuccess, inbox: true, newMessagesCount: 0 });
      
      await new Promise(resolve => setTimeout(resolve, 500));

      setAuthState('success');
      toast.success(profileSuccess ? 'Account reconnected!' : 'Account reconnected! Profile may take a moment.');
      
      setTimeout(() => {
        onSuccess();
        onOpenChange(false);
      }, 1200);
    } catch (err) {
      console.error('Post-connection sync error:', err);
      // Still mark as success even if sync fails - connection succeeded
      setAuthState('success');
      toast.success('Account reconnected!');
      setTimeout(() => {
        onSuccess();
        onOpenChange(false);
      }, 1200);
    }
  };

  const startPolling = () => {
    const interval = setInterval(async () => {
      try {
        const { data: pending } = await supabase
          .from('pending_linkedin_connections')
          .select('status, client_id, error_message')
          .eq('unipile_account_id', accountId)
          .maybeSingle();

        if (pending) {
          if (pending.status === 'completed') {
            clearInterval(interval);
            setPollInterval(null);
            setTimeRemaining(0);
            await performPostConnectionSync();
          } else if (pending.status === 'failed') {
            clearInterval(interval);
            setPollInterval(null);
            setTimeRemaining(0);
            setAuthState('error');
            setError(pending.error_message || 'Verification failed. Please try again.');
          } else if (pending.status === 'expired') {
            clearInterval(interval);
            setPollInterval(null);
            setTimeRemaining(0);
            setAuthState('error');
            setError('Verification timed out. Please start over.');
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);

    setPollInterval(interval);
  };

  const handleAuthenticate = async () => {
    if (!password) {
      setError("Password is required");
      return;
    }

    setError("");
    setLoading(true);
    setAuthState('authenticating');

    try {
      // Get client's workspace
      const { data: client } = await supabase
        .from("clients")
        .select("workspace_id, country")
        .eq("id", clientId)
        .single();

      if (!client) {
        setAuthState('error');
        setError("Client not found");
        setLoading(false);
        return;
      }

      // Country must be lowercase for Unipile geographic routing (e.g., "in", "us")
      const countryCode = (client.country || "us").toLowerCase();
      console.log(`🌍 Reconnection country: stored="${client.country}" -> sending="${countryCode}"`);

      const { data, error: rawInvokeError } = await supabase.functions.invoke("linkedin-custom-auth", {
        body: {
          username: clientEmail,
          password: password,
          country: countryCode,
          workspaceId: client.workspace_id,
          isReconnect: true,
          clientId: clientId,
        },
      });
      const invokeError = await readInvokeError(rawInvokeError);

      if (invokeError || !data?.success) {
        const errorMessage = data?.message 
          || data?.error 
          || invokeError?.message 
          || "Failed to reconnect. Please try again.";
        
        // Filter out generic/leaked error messages
        let displayError = errorMessage.includes('Edge Function returned a non-2xx status code')
          ? "Failed to reconnect. Please try again."
          : errorMessage;
        if (displayError === "Unknown error" || displayError.toLowerCase().includes('unipile')) {
          displayError = "Reconnection failed. Please try a different region or try again later.";
        }
          
        setAuthState('error');
        setError(displayError);
        setLoading(false);
        return;
      }

      // Handle different response statuses
      if (data.status === 'connected' || data.status === 'reconnected') {
        setLoading(false);
        await performPostConnectionSync();
      } else if (data.status === 'checkpoint') {
        setAccountId(data.account_id);
        setCheckpointMessage(data.checkpoint_data?.message || '');
        
        switch (data.checkpoint_type) {
          case 'OTP':
            setAuthState('checkpoint_otp');
            setLoading(false);
            break;
          case '2FA':
            setAuthState('checkpoint_2fa');
            setLoading(false);
            break;
          case 'IN_APP_VALIDATION':
            // AUTO-BYPASS: Show seamless "Requesting verification code..." state
            // Users never see the IN_APP_VALIDATION screen
            console.log('🔄 IN_APP_VALIDATION detected - auto-bypassing to OTP');
            setAccountId(data.account_id);
            setAuthState('requesting_otp');
            // Immediately trigger Try Another Way - no delay needed
            handleTryAnotherWayAuto(data.account_id);
            return; // Exit early - handleTryAnotherWayAuto will handle the state
          case 'CAPTCHA':
            setAuthState('checkpoint_captcha');
            setLoading(false);
            break;
          default:
            setAuthState('checkpoint_otp');
            setLoading(false);
        }
      }
    } catch (err: any) {
      console.error("Error:", err);
      setAuthState('error');
      setError(err.message || "An unexpected error occurred");
      setLoading(false);
    }
  };

  const handleSolveCheckpoint = async () => {
    if (!otpCode) {
      setError("Please enter the verification code");
      return;
    }

    setError("");
    setLoading(true);

    try {
      const { data, error: rawInvokeError } = await supabase.functions.invoke("linkedin-solve-checkpoint", {
        body: {
          account_id: accountId,
          code: otpCode,
          isReconnect: true,
          clientId: clientId,
        },
      });
      const invokeError = await readInvokeError(rawInvokeError);

      if (invokeError || !data?.success) {
        const errorMessage = data?.message 
          || data?.error 
          || invokeError?.message 
          || "Verification failed. Please try again.";
        setError(errorMessage);
        setLoading(false);
        return;
      }

      if (data.status === 'connected' || data.status === 'reconnected') {
        setLoading(false);
        await performPostConnectionSync();
      } else if (data.status === 'checkpoint') {
        setOtpCode("");
        setCheckpointMessage(data.checkpoint_data?.message || '');
        
        switch (data.checkpoint_type) {
          case 'OTP':
            setAuthState('checkpoint_otp');
            setLoading(false);
            break;
          case '2FA':
            setAuthState('checkpoint_2fa');
            setLoading(false);
            break;
          case 'IN_APP_VALIDATION':
            // AUTO-BYPASS: Show seamless "Requesting verification code..." state
            console.log('🔄 IN_APP_VALIDATION in solve-checkpoint - auto-bypassing to OTP');
            setAccountId(data.account_id || accountId);
            setAuthState('requesting_otp');
            handleTryAnotherWayAuto(data.account_id || accountId);
            return; // Exit early
          case 'CAPTCHA':
            setAuthState('checkpoint_captcha');
            setLoading(false);
            break;
          default:
            setAuthState('checkpoint_otp');
            setLoading(false);
        }
      }
    } catch (err: any) {
      console.error("Error:", err);
      setError(err.message || "An unexpected error occurred");
      setLoading(false);
    }
  };

  const handleTryAnotherWay = async () => {
    setError("");
    setLoading(true);

    try {
      const { data, error: rawInvokeError } = await supabase.functions.invoke("linkedin-solve-checkpoint", {
        body: {
          account_id: accountId,
          code: "TRY_ANOTHER_WAY",
          isReconnect: true,
          clientId: clientId,
        },
      });
      const invokeError = await readInvokeError(rawInvokeError);

      if (invokeError) {
        setError(invokeError.message || "Failed to switch verification method");
        setLoading(false);
        return;
      }

      if (data?.status === 'checkpoint') {
        setOtpCode("");
        setCheckpointMessage(data.checkpoint_data?.message || '');
        
        switch (data.checkpoint_type) {
          case 'OTP':
            setAuthState('checkpoint_otp');
            break;
          case '2FA':
            setAuthState('checkpoint_2fa');
            break;
          case 'IN_APP_VALIDATION':
            setAuthState('checkpoint_in_app');
            setTimeRemaining(300);
            break;
          case 'CAPTCHA':
            setAuthState('checkpoint_captcha');
            break;
          default:
            setAuthState('checkpoint_otp');
        }
        toast.info('Switched to different verification method');
      }

      setLoading(false);
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
      setLoading(false);
    }
  };

  // Auto-bypass handler for IN_APP_VALIDATION - automatically requests OTP instead
  const handleTryAnotherWayAuto = async (autoAccountId: string) => {
    setLoading(true);
    
    try {
      const { data, error: rawInvokeError } = await supabase.functions.invoke("linkedin-solve-checkpoint", {
        body: {
          account_id: autoAccountId,
          code: "TRY_ANOTHER_WAY",
          isReconnect: true,
          clientId: clientId,
        },
      });
      const invokeError = await readInvokeError(rawInvokeError);

      if (!invokeError && data?.status === 'checkpoint') {
        setAccountId(autoAccountId);
        setOtpCode("");
        setCheckpointMessage(data.checkpoint_data?.message || '');

        switch (data.checkpoint_type) {
          case 'OTP':
            setAuthState('checkpoint_otp');
            toast.success('Enter the verification code');
            break;
          case '2FA':
            setAuthState('checkpoint_2fa');
            toast.success('Enter your 2FA code');
            break;
          default:
            // If we still get IN_APP_VALIDATION, fall back to showing it
            setAuthState('checkpoint_in_app');
            setTimeRemaining(300);
        }
      } else {
        // If auto-bypass fails, fall back to showing IN_APP_VALIDATION
        setAccountId(autoAccountId);
        setAuthState('checkpoint_in_app');
        setTimeRemaining(300);
      }
    } catch (err) {
      console.error('Auto try another way error:', err);
      // Fall back to showing IN_APP_VALIDATION
      setAccountId(autoAccountId);
      setAuthState('checkpoint_in_app');
      setTimeRemaining(300);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading && authState !== 'syncing_data') {
      resetState();
      onOpenChange(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const renderContent = () => {
    switch (authState) {
      case 'requesting_otp':
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <Loader2 className="h-10 w-10 text-primary animate-spin" />
            <div className="text-center space-y-1">
              <p className="font-medium">Requesting verification code...</p>
              <p className="text-sm text-muted-foreground">
                This will just take a moment
              </p>
            </div>
          </div>
        );

      case 'success':
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-4">
            <div className="h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-lg">Account Reconnected!</h3>
              <p className="text-muted-foreground text-sm">Your LinkedIn account has been successfully reconnected.</p>
            </div>
          </div>
        );

      case 'syncing_data':
        return (
          <div className="flex flex-col items-center justify-center py-8 space-y-6">
            <div className="h-16 w-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Loader2 className="h-8 w-8 text-blue-600 dark:text-blue-400 animate-spin" />
            </div>
            <div className="text-center">
              <h3 className="font-semibold text-lg">Setting Up Your Account</h3>
              <p className="text-muted-foreground text-sm">Please wait while we sync your data...</p>
            </div>
            <div className="w-full max-w-xs space-y-3">
              <div className="flex items-center gap-3">
                {syncProgress.profile ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
                )}
                <span className={syncProgress.profile ? "text-foreground" : "text-muted-foreground"}>
                  Fetching profile data
                </span>
              </div>
              <div className="flex items-center gap-3">
                {syncProgress.inbox ? (
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                ) : (
                  <Loader2 className="h-5 w-5 text-muted-foreground animate-spin" />
                )}
                <span className={syncProgress.inbox ? "text-foreground" : "text-muted-foreground"}>
                  Preparing account...
                </span>
              </div>
            </div>
          </div>
        );

      case 'checkpoint_in_app':
        return (
          <div className="space-y-6 py-4">
            <div className="flex flex-col items-center justify-center space-y-4">
              <div className="h-16 w-16 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center animate-pulse">
                <Smartphone className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="text-center">
                <h3 className="font-semibold text-lg">Check Your LinkedIn App</h3>
                <p className="text-muted-foreground text-sm mt-1">
                  {checkpointMessage || "Open the LinkedIn app on your phone and approve the login request."}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Clock className="h-4 w-4" />
                  Time remaining
                </span>
                <span className="font-medium">{formatTime(timeRemaining)}</span>
              </div>
              <Progress value={(timeRemaining / 300) * 100} className="h-2" />
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Waiting for you to approve the login in the LinkedIn mobile app. This page will update automatically.
              </AlertDescription>
            </Alert>

            <div className="flex justify-center">
              <Button variant="outline" size="sm" onClick={handleTryAnotherWay} disabled={loading}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Try another way
              </Button>
            </div>
          </div>
        );

      case 'checkpoint_captcha':
        return (
          <div className="space-y-4 py-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                LinkedIn is requesting a CAPTCHA verification. This cannot be completed automatically.
                Please try again later or use a different account.
              </AlertDescription>
            </Alert>
            <div className="text-center">
              <Button variant="outline" onClick={() => setAuthState('idle')}>
                Try Again
              </Button>
            </div>
          </div>
        );

      case 'checkpoint_otp':
      case 'checkpoint_2fa':
        return (
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="text-center space-y-2">
              <h3 className="font-semibold">
                {authState === 'checkpoint_2fa' ? 'Two-Factor Authentication' : 'Verification Code'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {checkpointMessage || "Enter the verification code to continue"}
              </p>
            </div>

            <div className="flex justify-center">
              <InputOTP
                maxLength={6}
                value={otpCode}
                onChange={setOtpCode}
                disabled={loading}
              >
                <InputOTPGroup>
                  <InputOTPSlot index={0} />
                  <InputOTPSlot index={1} />
                  <InputOTPSlot index={2} />
                  <InputOTPSlot index={3} />
                  <InputOTPSlot index={4} />
                  <InputOTPSlot index={5} />
                </InputOTPGroup>
              </InputOTP>
            </div>

            <div className="flex justify-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTryAnotherWay} disabled={loading}>
                Try another way
              </Button>
            </div>
          </div>
        );

      case 'error':
        return (
          <div className="space-y-4 py-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
            <div className="text-center">
              <Button variant="outline" onClick={() => setAuthState('idle')}>
                Try Again
              </Button>
            </div>
          </div>
        );

      default: // idle or authenticating
        return (
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">LinkedIn Email</Label>
              <Input
                id="email"
                type="email"
                value={clientEmail}
                disabled
                className="bg-muted"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">LinkedIn Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your LinkedIn password"
                  disabled={loading || authState === 'authenticating'}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && password) {
                      handleAuthenticate();
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Re-enter your LinkedIn password to reconnect. Your credentials are sent directly to LinkedIn and are not stored.
              </AlertDescription>
            </Alert>
          </div>
        );
    }
  };

  const renderFooter = () => {
    if (authState === 'success' || authState === 'syncing_data' || authState === 'checkpoint_in_app' || authState === 'checkpoint_captcha' || authState === 'requesting_otp') {
      return null;
    }

    if (authState === 'checkpoint_otp' || authState === 'checkpoint_2fa') {
      return (
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSolveCheckpoint} disabled={loading || otpCode.length !== 6}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </Button>
        </DialogFooter>
      );
    }

    if (authState === 'error') {
      return (
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      );
    }

    return (
      <DialogFooter>
        <Button variant="outline" onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleAuthenticate} disabled={loading || !password}>
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              Reconnecting...
            </>
          ) : (
            <>
              <RefreshCw className="h-4 w-4 mr-2" />
              Reconnect Account
            </>
          )}
        </Button>
      </DialogFooter>
    );
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Reconnect LinkedIn Account
          </DialogTitle>
          <DialogDescription>
            Re-authenticate your LinkedIn account to restore messaging capabilities.
          </DialogDescription>
        </DialogHeader>

        {renderContent()}
        {renderFooter()}
      </DialogContent>
    </Dialog>
  );
}
